import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
    Search,
    Funnel,
    Edit2,
    Plus,
    MoreHorizontal,
    ChevronsUpDown,
    ChevronLeft,
    ChevronRight,
    X,
    Copy,
    CircleCheck
} from 'lucide-react';
import { useHistory, useLocation } from "react-router-dom";
import { Info } from 'lucide-react';
import Tooltip from '../addentities/Tooltip';
import apiHelper from '../../apiHelper';
import { copyToClipboard } from '../../utils/helper';
import Pagination from '../../components/Pagination';
import SortableHeader from '../../components/SortableHeader';
import FilterDrawer from '../../components/FilterDrawer';
import { set } from 'date-fns';

// Dummy data (10 rows) matching the screenshot exactly
const dummyDataOffboarded = [

];

const PAGE_SIZE = 10; // Display all 10 rows on one page

export default function Employee() {
    const [activeTab, setActiveTab] = useState('current-employees');
    const [employeeData, setEmployeeData] = useState([]);
    const history = useHistory();
    const location = useLocation();
    const [successMessage, setSuccessMessage] = useState('');
    

    // Breadcrumbs (you can adjust these if needed)
    const breadcrumbs = ["Organisations", "Employees"];

    // Search, pagination, and multi‐selection state
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState([]);
    const [filterDepartments, setFilterDepartments] = useState([]);
    const [filterEntities, setFilterEntities] = useState([]);
    const [filterManagers, setFilterManagers] = useState([]);
    const [filterWorkplaces, setFilterWorkplaces] = useState([]);
    const [filterOnboardDate, setFilterOnboardDate] = useState([]);
    const [departmentSearchTerm, setDepartmentSearchTerm] = useState('');
    const [entitySearchTerm, setEntitySearchTerm] = useState('');
    const [managerSearchTerm, setManagerSearchTerm] = useState('');
    const [workplaceSearchTerm, setWorkplaceSearchTerm] = useState('');
    const [onboardDateSearchTerm, setOnboardDateSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedIds, setSelectedIds] = useState([]);
    // Offboarded Employees Tab
    const [offboardedSearchTerm, setOffboardedSearchTerm] = useState('');
    const [offboardedCurrentPage, setOffboardedCurrentPage] = useState(1);
    const [offboardedSelectedIds, setOffboardedSelectedIds] = useState([]);
    const [offboardedFilterWorkplaces, setOffboardedFilterWorkplaces] = useState([]);
    const [offboardedWorkplaceSearchTerm, setOffboardedWorkplaceSearchTerm] = useState('');

    // Bulk Edit Drawer (right‐side) state
    const [bulkEditOpen, setBulkEditOpen] = useState(false);
    const bulkDrawerRef = useRef(null);

    // “Suppress highlight” flag: if true, even checked rows will not be highlighted
    const [suppressHighlight, setSuppressHighlight] = useState(false);

    // Single‐row Drawer state (unchanged)
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerTab, setDrawerTab] = useState('entity-info');
    const [selectedRow, setSelectedRow] = useState(null);
    const [tempOffBoardedValues, setTempOffBoardedValues] = useState({
        workplaces: [],
        workplacesSearch: "",
    });
    const [tempValues, setTempValues] = useState({
        status: [],
        departments: [],
        entities: [],
        managers: [],
        workplaces: [],
        onboardDate: [],

        statusSearch: "",
        departmentsSearch: "",
        entitiesSearch: "",
        managersSearch: "",
        workplacesSearch: "",
        onboardDateSearch: "",
    });

    // Filtered list of rows, based on searchTerm
    const filtered = useMemo(() => {
        const term = searchTerm.toLowerCase();
        return employeeData.filter(d => {
            const matchesSearch =
                !searchTerm ||
                (d.uuid ? d.uuid.toLowerCase().includes(term) : false) ||
                (d.employee_first_name ? d.employee_first_name.toLowerCase().includes(term) : false) ||
                (d.employee_last_name ? d.employee_last_name.toLowerCase().includes(term) : false) ||
                (d.display_name ? d.display_name.toLowerCase().includes(term) : false) ||
                (d.company_email ? d.company_email.toLowerCase().includes(term) : false) ||
                (d.personal_email ? d.personal_email.toLowerCase().includes(term) : false) ||
                (d.job_title_data?.job_title_name ? d.job_title_data.job_title_name.toLowerCase().includes(term) : false) ||
                (d.notes ? d.notes.toLowerCase().includes(term) : false);
            const matchesStatus = filterStatus.length === 0 || filterStatus.includes(d.status_name);
            const matchesDepartments = filterDepartments.length === 0 || filterDepartments.some(dept => d.departments_names?.toLowerCase().includes(dept.toLowerCase()));
            const matchesEntities = filterEntities.length === 0 || filterEntities.some(entity => d.entities_names?.toLowerCase().includes(entity.toLowerCase()));
            const matchesManagers = filterManagers.length === 0 || filterManagers.some(manager => d.manager?.toLowerCase().includes(manager.toLowerCase()));
            const matchesWorkplaces = filterWorkplaces.length === 0 || filterWorkplaces.some(workplace => d.workplaces_names?.toLowerCase().includes(workplace.toLowerCase()));
            const matchesOnboardDate = filterOnboardDate.length === 0 || filterOnboardDate.includes(d.onboard_date);
            return matchesSearch && matchesStatus && matchesDepartments && matchesEntities && matchesManagers && matchesWorkplaces && matchesOnboardDate;
        });
    }, [searchTerm, employeeData, filterStatus, filterDepartments, filterEntities, filterManagers, filterWorkplaces, filterOnboardDate]);

    const offboardedFiltered = useMemo(() => {
        const term = offboardedSearchTerm.toLowerCase();
        return dummyDataOffboarded.filter(d =>
            d.name.toLowerCase().includes(term) ||
            d.email.toLowerCase().includes(term) ||
            d.department.toLowerCase().includes(term) ||
            d.manager.toLowerCase().includes(term) ||
            d.jobTitle.toLowerCase().includes(term) ||
            d.entity.toLowerCase().includes(term) ||
            d.workplace.toLowerCase().includes(term)
        ).filter(d =>
            offboardedFilterWorkplaces.length === 0 || offboardedFilterWorkplaces.some(w => d.workplace.toLowerCase().includes(w.toLowerCase()))
        );
    }, [offboardedSearchTerm, offboardedFilterWorkplaces]);

    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    const offboardedTotalPages = Math.ceil(offboardedFiltered.length / PAGE_SIZE);
    const offboardedPageData = offboardedFiltered.slice((offboardedCurrentPage - 1) * PAGE_SIZE, offboardedCurrentPage * PAGE_SIZE);

    const [allPermissionGroups, setAllPermissionGroups] = useState([
        { role_name: 'Admin', id: '1' },
        { role_name: 'Editor', id: '2' },
        { role_name: 'Viewer', id: '3' },
        { role_name: 'IT', id: '4' },
    ]);
    const [allDepartments, setAllDepartments] = useState([
        { short_name: 'HQ', department_id: '1' },
        { short_name: 'Mister Mobile HQ', department_id: '2' },
        { short_name: 'Mister Mobile HQ 2', department_id: '3' },
    ]);
    const [allEntities, setAllEntities] = useState([
        { entity_short_name: 'HQ', entity_id: '1' },
        { entity_short_name: 'Mister Mobile HQ', entity_id: '2' },
        { entity_short_name: 'Mister Mobile HQ 2', entity_id: '3' },
    ]);
    const [allWorkplaces, setAllWorkplaces] = useState([
        { short_name: 'HQ', workplace_id: '1' },
        { short_name: 'Mister Mobile HQ', workplace_id: '2' },
        { short_name: 'Mister Mobile HQ 2', workplace_id: '3' },
    ]);
    const [allManagers, setAllManagers] = useState([
        { name: 'Abc', id: '1' },
        { name: 'Def', id: '2' },
        { name: 'Ghi', id: '3' },
    ]);

    // Toggle selecting a single row (checkbox)
    const toggleSelect = id => {
        setSelectedIds(ids => {
            const newIds = ids.includes(id)
                ? ids.filter(x => x !== id)
                : [...ids, id];

            // As soon as user toggles a checkbox, we should no longer suppress highlight
            setSuppressHighlight(false);
            return newIds;
        });
    };

    const toggleOffboardedSelect = id => {
        setOffboardedSelectedIds(ids => {
            const newIds = ids.includes(id)
                ? ids.filter(x => x !== id)
                : [...ids, id];

            setSuppressHighlight(false);
            return newIds;
        });
    };

    // Toggle “select all” on the current page
    const toggleAll = () => {
        setSelectedIds(prev => {
            const next =
                prev.length === pageData.length
                    ? []
                    : pageData.map(d => d.id);

            // As soon as user toggles “select all,” remove the suppression
            setSuppressHighlight(false);
            return next;
        });
    };

    const toggleOffboardedAll = () => {
        setOffboardedSelectedIds(prev => {
            const next =
                prev.length === offboardedPageData.length
                    ? []
                    : offboardedPageData.map(d => d.id);

            setSuppressHighlight(false);
            return next;
        });
    };

    // Whenever selectedIds changes, open or close the Bulk Edit drawer
    useEffect(() => {
        if (selectedIds.length > 0) {
            // setBulkEditOpen(true);
        } else {
            setBulkEditOpen(false);
        }
    }, [selectedIds]);

    // Close Bulk Edit drawer (HIDE it) when clicking outside
    // IMPORTANT: We do NOT clear selectedIds here! We only want to hide it,
    // and suppress row highlighting, until next user checkbox interaction.
    useEffect(() => {
        function handleClickOutsideBulk(event) {
            if (
                bulkEditOpen &&
                bulkDrawerRef.current &&
                !bulkDrawerRef.current.contains(event.target)
            ) {
                setBulkEditOpen(false);
                // **Do not clear `selectedIds` here.** Just suppress highlight until next toggle.
                setSuppressHighlight(true);
            }
        }
        document.addEventListener('mousedown', handleClickOutsideBulk);
        return () => document.removeEventListener('mousedown', handleClickOutsideBulk);
    }, [bulkEditOpen]);

    // When a row (except its checkbox) is clicked, open the single‐row drawer
    const onRowClick = (row) => {
        setSelectedRow(row);
        setDrawerTab('entity-info');
        setDrawerOpen(true);
    };

    // Remove one selected ID (removes its tag and unchecks the row)
    const removeSelectedId = (id) => {
        setSelectedIds(ids => {
            const next = ids.filter(x => x !== id);
            // Removing a tag is basically the same as unchecking a box: we can resume highlighting
            setSuppressHighlight(false);
            return next;
        });
    };

    const removeOffboardedSelectedId = (id) => {
        setOffboardedSelectedIds(ids => {
            const next = ids.filter(x => x !== id);
            setSuppressHighlight(false);
            return next;
        });
    };

    // Bulk Edit “Cancel”: close drawer & uncheck all
    const handleBulkCancel = () => {
        setBulkEditOpen(false);
        setSelectedIds([]);
        setSuppressHighlight(false);
    };

    // Bulk Edit “Save”: stubbed—logs to console and closes
    const handleBulkSave = () => {
        console.log("Bulk saved for IDs:", selectedIds);
        setBulkEditOpen(false);
        setSelectedIds([]);
        setSuppressHighlight(false);
    };

    // Dropdown (ellipsis) state & refs (unchanged)
    const [dropdownOpenId, setDropdownOpenId] = useState(null);
    const dropdownRef = useRef(null);
    const buttonRefs = useRef({});
    const drawerRef = useRef(null);

    // Close single‐row Drawer when clicking outside
    useEffect(() => {
        function handleClickOutsideDrawer(event) {
            if (
                drawerOpen &&
                drawerRef.current &&
                !drawerRef.current.contains(event.target)
            ) {
                setDrawerOpen(false);
                setSelectedRow(null);
                // If we click outside the single‐row drawer, we unselect that row but leave bulk selections alone
                setSelectedIds(ids =>
                    selectedRow ? ids.filter(id => id !== selectedRow.id) : ids
                );
            }
        }
        document.addEventListener('mousedown', handleClickOutsideDrawer);
        return () =>
            document.removeEventListener('mousedown', handleClickOutsideDrawer);
    }, [drawerOpen, selectedRow]);

    // Dropdown positioning state
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

    // Toggle the per‐row dropdown menu
    const toggleDropdown = (id) => {
        if (dropdownOpenId === id) {
            setDropdownOpenId(null);
        } else {
            const btn = buttonRefs.current[id];
            if (btn) {
                const rect = btn.getBoundingClientRect();
                const dropdownWidth = 150;
                const dropdownHeight = 100;

                let left = rect.left + window.scrollX - dropdownWidth + rect.width;
                if (left < window.scrollX + 8) {
                    left = window.scrollX + 8;
                }

                let top = rect.bottom + window.scrollY;
                if (top + dropdownHeight > window.scrollY + window.innerHeight) {
                    top = rect.top + window.scrollY - dropdownHeight;
                }

                setDropdownPosition({ top, left });
            }
            setDropdownOpenId(id);
        }
    };
    const offboardRef = useRef(null);
    const [offboardModalOpen, setOffboardModalOpen] = useState(false);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null); // optional
    const handleOffboardConfirm = () => {
        // Your logic here (e.g., API call, toast, state update)
        console.log("Offboarding employee:", selectedEmployeeId);
        setOffboardModalOpen(false);
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (offboardRef.current && !offboardRef.current.contains(event.target)) {
                setOffboardModalOpen(false);
        }
        }

        if (offboardModalOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [offboardModalOpen]);

    useEffect(() => {
        async function fetchInitialData() {
            try {
                // Parallel calls for dependencies
                const [
                    departmentsRes,
                    entitiesRes,
                    workplacesRes,
                    permissionGroupsRes
                ] = await Promise.all([
                    apiHelper('departments/list/'),
                    apiHelper('entities/list/'),
                    apiHelper('entities/workplaces/'),
                    apiHelper('roles/list/'),
                ]);

                // Wait for all JSON conversions
                const [
                    departmentsSet,
                    entitiesSet,
                    workplacesSet,
                    permissionGroupsSet
                ] = await Promise.all([
                    departmentsRes.ok ? departmentsRes.json() : [],
                    entitiesRes.ok ? entitiesRes.json() : [],
                    workplacesRes.ok ? workplacesRes.json() : [],
                    permissionGroupsRes.ok ? permissionGroupsRes.json() : [],
                ]);

                // Set state for all
                setAllDepartments(departmentsSet);
                setAllEntities(entitiesSet);
                setAllWorkplaces(workplacesSet);
                setAllPermissionGroups(permissionGroupsSet);
                console.log("workplaces",workplacesSet)
                // Now fetch employees
                const employeesRes = await apiHelper('users/list/');
                if (employeesRes.ok) {
                    const result = await employeesRes.json();
                    // if (response && response.ok) {
                    //     const result = await response.json();
                    setAllManagers(result)
                    // console.log(result);
                    let sampleData = result;

                    sampleData = sampleData.map(item => {
                        const manager = allManagers.find(m => m.display_name === item.manager?.toString());
                        const departmentNames = item.departments?.map(id => {
                            const departments = departmentsSet.find(m => m.department_id == id.toString());
                            return departments ? departments.short_name : '';
                        });
                        const workplacesNames = item.workplaces?.map(id => {
                            const workplaces = workplacesSet.find(m => m.workplace_id == id.toString());
                            console.log(workplaces);
                            return workplaces ? workplaces.workplace_short_name : '-';
                        });
                        const entitiesNames = item.entities?.map(id => {
                            const entities = entitiesSet.find(m => m.entity_id == id.toString());
                            return entities ? entities.entity_short_name : '';
                        });

                        return {
                            ...item,
                            status_name: item.status == 1 ? 'Active' : 'Inactive',
                            manager_name: manager ? manager.display_name : '',
                            departments_names: departmentNames ? departmentNames.join(', ') : '-',
                            workplaces_names: workplacesNames ? workplacesNames.join(', ') : '-',
                            entities_names: entitiesNames ? entitiesNames.join(', ') : '-',
                        };
                    });
                    setEmployeeData(sampleData);
                    // }
                    // setEmployeeData(employees);
                }

            } catch (error) {
                console.error('Failed to fetch initial data:', error);
            }
        }

        fetchInitialData();
    }, []);

    const editEmployee = () => {

        if (selectedIds.length == 1) {
            history.push(`/employee/edit-employee/${selectedIds[0]}`)
        }
        else if (dropdownOpenId) {
            history.push(`/employee/edit-employee/${dropdownOpenId}`)
        }

    }

   
    const openIdRef = useRef(null);
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        if (openIdRef.current) {
        navigator.clipboard.writeText(openIdRef.current.innerText);
        setCopied(true);
        setTimeout(() => setCopied(false), 1000); // reset after 1 sec
        }
    };
    const filterButtonRef = useRef(null);
    const filterPopupRef = useRef(null);
    const [filterPopupOpen, setFilterPopupOpen] = useState(false);
    const [filterPosition, setFilterPosition] = useState({ top: 0, left: 0 });
    const formattedDate = new Date(selectedRow?.onboard_date).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
    });
    const handleCheckboxChange = (setter, value) => (e) => {
        const isChecked = e.target.checked;
        setter((prev) => (isChecked ? [...prev, value] : prev.filter((v) => v !== value)));
        setCurrentPage(1);
    };
    useEffect(() => {
    function handleClickOutsideDropdown(event) {
        if (
            dropdownOpenId !== null &&
            dropdownRef.current &&
            !dropdownRef.current.contains(event.target) &&
            !Object.values(buttonRefs.current).some(btn => btn?.contains(event.target))
        ) {
            setDropdownOpenId(null);
        }
    }
    document.addEventListener('mousedown', handleClickOutsideDropdown);
    return () => document.removeEventListener('mousedown', handleClickOutsideDropdown);
    }, [dropdownOpenId]);
    const handleDropdownOptionClick = () => {
    setDropdownOpenId(null);
    };
    const handleOffboardedCheckboxChange = (setter, value) => (e) => {
        const isChecked = e.target.checked;
        setter((prev) => (isChecked ? [...prev, value] : prev.filter((v) => v !== value)));
        setOffboardedCurrentPage(1);
    };

    useEffect(() => {
        if (filterPopupOpen && filterButtonRef.current) {
            const rect = filterButtonRef.current.getBoundingClientRect();
            setFilterPosition({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
        }
    }, [filterPopupOpen]);

    useEffect(() => {
        function handleClickOutsideFilter(event) {
            if (filterPopupOpen && filterPopupRef.current && !filterPopupRef.current.contains(event.target) && !filterButtonRef.current.contains(event.target)) {
                setFilterPopupOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutsideFilter);
        return () => document.removeEventListener('mousedown', handleClickOutsideFilter);
    }, [filterPopupOpen]);

    const filterCount = [...filterStatus, ...filterDepartments, ...filterEntities, ...filterManagers, ...filterWorkplaces, ...filterOnboardDate].filter(Boolean).length;

     useEffect(() => {
            // debug log to check what's coming in
            console.log('location.state on mount:', location.state);
    
            if (location && location.state && location.state.success) {
                setSuccessMessage(location.state.success);
    
                // clear that location.state so it doesn't reappear on refresh/back
                // for react-router v5: history.replace(pathname, state)
                history.replace(location.pathname, {});
    
                // auto-hide the success message after 3-4s
                const t = setTimeout(() => setSuccessMessage(''), 4000);
                return () => clearTimeout(t);
            }
        }, [location, history]);
    
    
        useEffect(() => {
            if (successMessage) {
                const t = setTimeout(() => setSuccessMessage(""), 4000);
                return () => clearTimeout(t);
            }
        }, [successMessage]);

        const FilterActiveIcon = () => (
        <svg style={{ margin: 0 }} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20.6325 6.44444C20.7252 6.24267 20.7746 6.02858 20.8107 5.74756C21.0354 4.00089 21.1478 3.12756 21.6356 2.56378C20.1234 2 19.2177 2 17.4063 2H4.59369C2.78227 2 1.87655 2 1.36439 2.56378C0.852232 3.12756 0.964572 4.00089 1.18925 5.74756C1.25487 6.25766 1.36399 6.54725 1.69704 6.94296C2.77293 8.22128 4.74334 10.4951 7.50829 12.5623C7.76095 12.7512 7.92676 13.0599 7.95475 13.4016C8.26623 17.2071 8.55159 19.8068 8.70309 21.0865C8.78256 21.7577 9.52673 22.274 10.14 21.8404C11.1702 21.1118 13.0947 20.2952 13.349 18.938C13.4692 18.2964 13.6451 17.1765 13.8437 15.3333" stroke="#0D6EFD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <g clipPath="url(#clip0_4516_4830)">
                <path d="M22.8125 11C22.8125 14.2102 20.2102 16.8125 17 16.8125C13.7898 16.8125 11.1875 14.2102 11.1875 11C11.1875 7.78987 13.7898 5.18753 17 5.18753C20.2102 5.18753 22.8125 7.78987 22.8125 11ZM16.3277 14.0777L20.6402 9.7652C20.7866 9.61877 20.7866 9.38132 20.6402 9.23488L20.1099 8.70456C19.9634 9.5581 19.726 9.5581 19.5795 8.70456L16.0625 12.2215L14.4205 10.5795C14.2741 10.4331 14.0366 10.4331 13.8901 10.5795L13.3598 11.1099C13.2134 11.2563 13.2134 11.4937 13.3598 11.6402L15.7973 14.0777C15.9438 14.2241 16.1812 14.2241 16.3277 14.0777Z" fill="#0D6EFD" />
            </g>
            <defs>
                <clipPath id="clip0_4516_4830">
                    <rect width="12" height="12" fill="white" transform="translate(11 5)" />
                </clipPath>
            </defs>
        </svg>
    );

    const FilterInactiveIcon = () => (
        <svg style={{ margin: 0 }} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M21.6323 6.44444C21.7251 6.24267 21.7744 6.02858 21.8106 5.74756C22.0353 4.00089 22.1476 3.12756 21.6354 2.56378C21.1233 2 20.2176 2 18.4062 2H5.59366C3.78225 2 2.87655 2 2.36439 2.56378C1.85223 3.12756 1.96457 4.00089 2.18925 5.74756C2.25487 6.25766 2.36399 6.54725 2.69703 6.94296C3.77291 8.22128 5.74331 10.4951 8.50823 12.5623C8.76089 12.7512 8.9267 13.0599 8.95469 13.4016C9.26616 17.2071 9.55152 19.8068 9.70302 21.0865C9.78249 21.7577 10.5267 22.274 11.1399 21.8404C12.1701 21.1118 14.0946 20.2952 14.3489 18.938C14.4691 18.2964 14.645 17.1765 14.8436 15.3333" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M18.1113 7.5556V15.3334M22.0001 11.4445L14.2224 11.4445" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    const workplaceSections = [
  {
    key: "workplaces",
    label: "Workplaces",
    type: "list",

    // ensure always array
    data: (allWorkplaces || []).filter(item => item?.short_name),

    renderItem: (item) => ({
      id: item?.short_name || "",
      title: item?.short_name || "No name",
    }),
  },
];
const employeeFilterSections = [
  // STATUS
  {
    key: "status",
    label: "Status",
    type: "checkbox",
    options: ["Active", "Inactive"],
  },

  // DEPARTMENTS
  {
    key: "departments",
    label: "Departments",
    type: "list",
    data: allDepartments,
    renderItem: (item) => ({
      id: item.short_name,
      title: item.short_name,
    }),
  },

  // ENTITIES
  {
    key: "entities",
    label: "Entities",
    type: "list",
    data: allEntities,
    renderItem: (item) => ({
      id: item.entity_short_name,
      title: item.entity_short_name,
    }),
  },

  // MANAGERS
  {
    key: "managers",
    label: "Managers",
    type: "list",
    data: allManagers,
    renderItem: (item) => ({
      id: item.display_name,
      title: item.display_name,
    }),
  },

  // WORKPLACES
  {
    key: "workplaces",
    label: "Workplaces",
    type: "list",
    data: allWorkplaces,
    renderItem: (item) => ({
      id: item.workplace_short_name,
      title: item.workplace_short_name,
    }),
  },

  // ONBOARD DATE
  {
    key: "onboardDate",
    label: "Onboard Date",
    type: "list",
    data: [...new Map(
      employeeData.map(e => [
        e.onboard_date,
        { onboard_date: e.onboard_date }
      ])
    ).values()],
    renderItem: (item) => ({
      id: item.onboard_date,
      title: item.onboard_date,
    }),
  },
];

    return (
        <div className="company-body">
            {/* Breadcrumbs */}
            <div className="breadcrumb">
                {breadcrumbs.map((crumb, index) => (
                    <span key={index} className="breadcrumb-item">
                        {crumb}
                        {index !== breadcrumbs.length - 1}
                    </span>
                ))}
            </div>
             {successMessage && (
                <div
                    className="d-flex align-items-center justify-content-between"
                    role="alert"
                    style={{
                        boxShadow: "0px 4px 10px 0px #0000001F",
                        padding: "15px 10px",
                        borderRadius: "12px",
                        border: "1px solid #30B473",
                        backgroundColor: "#E6F7EF",
                        color: "#0D6438",
                        fontSize: "12px",
                        position: "fixed",
                        top: "10px",
                        width: "calc(100% - 20px)",
                        maxWidth: "640px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 2000,
                    }}
                >
                    <div className="d-flex align-items-center">
                        <CircleCheck className="me-2" />
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                            }}>
                            <strong>{successMessage}</strong>
                        </div>
                    </div>
     <X
                        size={16}
                        style={{ cursor: "pointer" }}
                        onClick={() => setSuccessMessage("")}
                    />
                </div>
            )}

            <div className="employee-tabs-container">
                {/* ─── Tab Buttons ─── */}
                <div className="tabs">
                    <button
                        // Apply the "active" class only when this tab is selected
                        className={`tab ${activeTab === 'current-employees' ? 'active' : ''
                            }`}
                        onClick={() => setActiveTab('current-employees')}
                    >
                        Current Employees
                    </button>

                    <button
                        className={`tab ${activeTab === 'offboarded-employees' ? 'active' : ''
                            }`}
                        onClick={() => setActiveTab('offboarded-employees')}
                    >
                        Offboarded Employees
                    </button>
                </div>

                {/* ─── Tab Contents ─── */}
                {activeTab === 'current-employees' && (
                    <div className="tab-content" id="current-employees-content">
                        {/* Current Employees Card */}
                        <div className="info-section">
                            <div className="info-card">
                                <div className="info-header">
                                    <h4 className="info-title">Current Employees</h4>
                                </div>

                                <div className="entities-tab">
                                    {/* Toolbar: Search + Filter on left; Edit/Plus on right */}
                                    <div className="toolbar">
                                        <div className="toolbar-left">
                                            <div className="search-box">
                                                <Search size={20} />
                                                <input
                                                    type="text"
                                                    placeholder="Search…"
                                                    value={searchTerm}
                                                    onChange={e => {
                                                        setSearchTerm(e.target.value);
                                                        setCurrentPage(1);
                                                    }}
                                                />
                                            </div>
                                            {/* <button
                                                ref={filterButtonRef}
                                                className="icon-btn new-look"
                                                onClick={() => setFilterPopupOpen(!filterPopupOpen)}
                                            >
                                                <Funnel size={18} /> Filter
                                                {filterCount > 0 && (
                                                    <span style={{ background: '#000', color: '#ffffff', borderRadius: '50%', width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', marginLeft: '5px' }}>
                                                        {filterCount}
                                                    </span>
                                                )}
                                            </button> */}
                                            <button
                                                ref={filterButtonRef}
                                                // className={`icon-btn p-2 filter-btn ${filtersApplied ? 'applied' : ''}`}
                                                className="icon-btn p-2 filter-btn "
                                                onClick={() => setFilterPopupOpen(!filterPopupOpen)}   // ← Block clicks
                                                // disabled={isTableEmpty}                 // ← NEW

                                                style={{
                                                    // border: filtersApplied ? '1px solid #2C7BE5' : '1px solid #6C757D',
                                                    border: '1px solid #6C757D',
                                                    borderWidth: '1px',
                                                    // color: filtersApplied ? '#2C7BE5' : '#6C757D',
                                                    color: '#6C757D',
                                                    // background: isTableEmpty
                                                    //   ? '#E9ECEF'                     // ← background when empty
                                                    //   : filtersApplied
                                                    //     ? '#CFE2FF'
                                                    //     : '#FFFFFF',
                                                    background: '#FFFFFF',
                                                    height: '40px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    // opacity: isTableEmpty ? 0.6 : 1,
                                                    // pointerEvents: isTableEmpty ? 'none' : 'auto',
                                                    opacity: 1,
                                                    pointerEvents: 'auto',
                                                }}
                                            >
                                                {/* {filtersApplied && !isTableEmpty ? <FilterActiveIcon /> : <FilterInactiveIcon />} */}
                                                <FilterInactiveIcon />

                                            </button>
                                            <FilterDrawer
                                                isOpen={filterPopupOpen}
                                                onClose={() => setFilterPopupOpen(false)}
                                                sections={employeeFilterSections}
                                                tempValues={tempValues}
                                                setTempValues={setTempValues}
                                                onApply={() => {
                                                    setFilterStatus(tempValues.status || []);
                                                    setFilterDepartments(tempValues.departments || []);
                                                    setFilterEntities(tempValues.entities || []);
                                                    setFilterManagers(tempValues.managers || []);
                                                    setFilterWorkplaces(tempValues.workplaces || []);
                                                    setFilterOnboardDate(tempValues.onboardDate || []);

                                                    setFilterPopupOpen(false);
                                                }}
                                                onClear={() => {
                                                    const empty = {
                                                        status: [],
                                                        departments: [],
                                                        entities: [],
                                                        managers: [],
                                                        workplaces: [],
                                                        onboardDate: [],
                                                    };

                                                    setTempValues(empty);

                                                    setFilterStatus([]);
                                                    setFilterDepartments([]);
                                                    setFilterEntities([]);
                                                    setFilterManagers([]);
                                                    setFilterWorkplaces([]);
                                                    setFilterOnboardDate([]);
                                                }}
                                            />
                                        </div>
                                        <div className="toolbar-right">
                                            <button className="icon-btn"><MoreHorizontal size={18} /></button>
                                            <button className="icon-btn" onClick={editEmployee}><Edit2 size={18} /></button>
                                            <button className="icon-btn" onClick={() => history.push('/employee/add-employee')}><Plus size={18} /></button>
                                        </div>
                                    </div>

                                    <div className="table-wrapper">
                                        <table className="entities-table">
                                            <thead>
                                                <tr>
                                                    <th>
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                checked={selectedIds.length === pageData.length && pageData.length > 0}
                                                                onChange={toggleAll}
                                                            />
                                                        </div>
                                                    </th>
                                                    <SortableHeader
                                                    label="Name"></SortableHeader>
                                                    <SortableHeader
                                                    label="Status"></SortableHeader>
                                                    <SortableHeader
                                                    label="Department"></SortableHeader>
                                                    <SortableHeader
                                                    label="Manager"></SortableHeader>
                                                    <SortableHeader
                                                    label="Job Title"></SortableHeader>
                                                    <SortableHeader
                                                    label="Workplace"></SortableHeader>
                                                    <th style={{ textAlign: 'center' }}>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pageData.map((row, idx) => {
                                                    const isRowSelected = selectedIds.includes(row.id) && !bulkEditOpen && !suppressHighlight;
                                                    return (
                                                        <tr
                                                            key={row.id}
                                                            className={[idx % 2 ? 'striped' : '', isRowSelected ? 'row-selected' : ''].join(' ')}
                                                            onClick={() => onRowClick(row)}
                                                        >
                                                            <td>
                                                                <div className="form-check" onClick={(e) => e.stopPropagation()}>
                                                                    <input
                                                                        type="checkbox"
                                                                        className="form-check-input"
                                                                        checked={selectedIds.includes(row.id)}
                                                                        onChange={(e) => {
                                                                            toggleSelect(row.id);
                                                                            if (e.target.checked) setSelectedRow(row);
                                                                            else if (selectedRow?.id === row.id) setSelectedRow(null);
                                                                        }}
                                                                    />
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="name-cell">
                                                                    <img
                                                                        src={row.avatar_url}
                                                                        alt="avatar"
                                                                        className="avatar"
                                                                        style={{ width: 28, height: 28, borderRadius: '50%', marginRight: 8, verticalAlign: 'middle' }}
                                                                    />
                                                                    {row.employee_first_name + ' ' + row.employee_last_name }
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <span className={`status ${row.status_name.toLowerCase()}`}>{row.status_name || <span style={{ color: '#ADB5BD' }}>NA</span>}</span>
                                                            </td>
                                                            <td>{row.departments_names || <span style={{ color: '#ADB5BD' }}>NA</span>}</td>
                                                            <td>{row.manager || <span style={{ color: '#ADB5BD' }}>NA</span>}</td>
                                                            <td>{row.job_title_data?.job_title_name || <span style={{ color: '#ADB5BD' }}>NA</span>}</td>
                                                            <td>{row.workplaces_names || <span style={{ color: '#ADB5BD' }}>NA</span>}</td>
                                                            <td style={{ textAlign: 'center', position: 'relative' }}>
                                                                <button
                                                                    ref={(el) => (buttonRefs.current[row.id] = el)}
                                                                    className="icon-btn"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        toggleDropdown(row.id);
                                                                    }}
                                                                >
                                                                    <MoreHorizontal size={20} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                                {pageData.length < PAGE_SIZE &&
                                                    [...Array(PAGE_SIZE - pageData.length)].map((_, idx) => (
                                                        <tr key={`empty-${idx}`} className="empty-row" onClick={() => { /* Do nothing */ }}>
                                                            <td colSpan={8}>&nbsp;</td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>

                                        {dropdownOpenId !== null && (
                                            <div
                                                ref={dropdownRef}
                                                className="backdrop-cls"
                                                style={{
                                                    position: 'absolute',
                                                    top: dropdownPosition.top,
                                                    left: dropdownPosition.left,
                                                    background: 'white',
                                                    boxShadow: '0px 1px 4px 0px #0000001A',
                                                    zIndex: 1000,
                                                    minWidth: 152,
                                                    borderRadius: 8,
                                                    padding: 12,
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <div className="btn-align-forall">
                                                    <div className="common-for-all">
                                                        <button
                                                            onClick={() => {
                                                                handleDropdownOptionClick();
                                                                const row = pageData.find((d) => d.id === dropdownOpenId);
                                                                onRowClick(row);
                                                            }}
                                                        >
                                                            View Details
                                                        </button>
                                                    </div>
                                                    <div className="common-for-all">
                                                        <button onClick={() => { handleDropdownOptionClick(); editEmployee(); }}>Edit</button>
                                                    </div>
                                                    <div className="common-for-all">
                                                        <button onClick={() => { handleDropdownOptionClick(); alert('Suspend clicked'); }}>Suspend</button>
                                                    </div>
                                                    <div className="common-for-all">
                                                        <button
                                                            onClick={() => {
                                                                handleDropdownOptionClick();
                                                                setOffboardModalOpen(true);
                                                            }}
                                                            className="deactivate-cls"
                                                        >
                                                            Offboard
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {filtered.length > 0 && (
                                        <div className="pagination">
                                            <div className="pagination-info">
                                                {` ${(currentPage - 1) * PAGE_SIZE + 1} - ${Math.min(
                                                    currentPage * PAGE_SIZE,
                                                    filtered.length
                                                )} of ${filtered.length}`}
                                            </div>

                                            <Pagination
                                                currentPage={currentPage}
                                                totalPages={totalPages}
                                                onPageChange={setCurrentPage}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'offboarded-employees' && (
                    <div className="tab-content" id="offboarded-employees-content">
                        <div className="info-section">
                            <div className="info-card">
                                <div className="info-header">
                                    <h4 className="info-title">Offboarded Employees</h4>
                                </div>

                                <div className="entities-tab">
                                    <div className="toolbar">
                                        <div className="toolbar-left">
                                            <div className="search-box">
                                                <Search size={20} />
                                                <input
                                                    type="text"
                                                    placeholder="Search…"
                                                    value={offboardedSearchTerm}
                                                    onChange={(e) => {
                                                        setOffboardedSearchTerm(e.target.value);
                                                        setOffboardedCurrentPage(1);
                                                    }}
                                                />
                                            </div>
                                            {/* <button
                                                ref={filterButtonRef}
                                                className="icon-btn new-look"
                                                onClick={() => setFilterPopupOpen(!filterPopupOpen)}
                                            >
                                                <Funnel size={18} /> Filter
                                                {offboardedFilterWorkplaces.length > 0 && (
                                                    <span style={{ background: '#000', color: '#ffffff', borderRadius: '50%', width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', marginLeft: '5px' }}>
                                                        {offboardedFilterWorkplaces.length}
                                                    </span>
                                                )}
                                            </button> */}
                                            <button
                                                ref={filterButtonRef}
                                                // className={`icon-btn p-2 filter-btn ${filtersApplied ? 'applied' : ''}`}
                                                className="icon-btn p-2 filter-btn "
                                                onClick={() => setFilterPopupOpen(!filterPopupOpen)}   // ← Block clicks
                                                // disabled={isTableEmpty}                 // ← NEW

                                                style={{
                                                    // border: filtersApplied ? '1px solid #2C7BE5' : '1px solid #6C757D',
                                                    border: '1px solid #6C757D',
                                                    borderWidth: '1px',
                                                    // color: filtersApplied ? '#2C7BE5' : '#6C757D',
                                                    color: '#6C757D',
                                                    // background: isTableEmpty
                                                    //   ? '#E9ECEF'                     // ← background when empty
                                                    //   : filtersApplied
                                                    //     ? '#CFE2FF'
                                                    //     : '#FFFFFF',
                                                    background: '#FFFFFF',
                                                    height: '40px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    // opacity: isTableEmpty ? 0.6 : 1,
                                                    // pointerEvents: isTableEmpty ? 'none' : 'auto',
                                                    opacity: 1,
                                                    pointerEvents: 'auto',
                                                }}
                                            >
                                                {/* {filtersApplied && !isTableEmpty ? <FilterActiveIcon /> : <FilterInactiveIcon />} */}
                                                <FilterInactiveIcon />

                                            </button>
                                            <FilterDrawer
                                                isOpen={filterPopupOpen}
                                                onClose={() => setFilterPopupOpen(false)}
                                                sections={workplaceSections}
                                                tempValues={tempOffBoardedValues}
                                                setTempValues={setTempOffBoardedValues}
                                                onApply={() => {
                                                    setOffboardedFilterWorkplaces(tempOffBoardedValues?.workplaces || []);
                                                    setFilterPopupOpen(false);
                                                }}
                                                onClear={() => {
                                                    const empty = {
                                                        workplaces: [],
                                                        workplacesSearch: "",
                                                    };

                                                    setTempOffBoardedValues(empty);
                                                    setOffboardedFilterWorkplaces([]);
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="table-wrapper">
                                        <table className="entities-table">
                                            <thead>
                                                <tr>
                                                    <th>
                                                        <div
                                                            className="form-check"
                                                            onClick={(e) => e.stopPropagation()}
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                        >
                                                            <input className="form-check-input" type="checkbox" checked={offboardedSelectedIds.length === offboardedPageData.length && offboardedPageData.length > 0} onChange={toggleOffboardedAll} />
                                                        </div>
                                                    </th>
                                                    <SortableHeader
                                                        label="Name"
                                                    >

                                                    </SortableHeader>

                                                    <SortableHeader
                                                        label="Email"
                                                    >
                                                    </SortableHeader>
                                                    <SortableHeader
                                                        label="Department"
                                                    >
                                                    </SortableHeader>
                                                    <SortableHeader
                                                        label="Manager"
                                                    >
                                                    </SortableHeader>
                                                    <SortableHeader
                                                        label="Job Title"
                                                    >
                                                    </SortableHeader>
                                                    <SortableHeader
                                                        label="Entity">
                                                    </SortableHeader>
                                                    <SortableHeader
                                                        label="Workplace">
                                                    </SortableHeader>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {offboardedPageData.map((row, idx) => {
                                                    const isRowSelected = offboardedSelectedIds.includes(row.id) && !bulkEditOpen && !suppressHighlight;
                                                    return (
                                                        <tr
                                                            key={row.id}
                                                            className={[idx % 2 ? 'striped' : '', isRowSelected ? 'row-selected' : ''].join(' ')}
                                                            onClick={() => onRowClick(row)}
                                                        >
                                                            <td>
                                                                <div
                                                                    className="form-check"
                                                                    onClick={(e) => e.stopPropagation()} // prevents tr.onClick
                                                                    onMouseDown={(e) => e.stopPropagation()} // prevents some browsers from firing tr events
                                                                >
                                                                    <input type="checkbox" className="form-check-input" checked={offboardedSelectedIds.includes(row.id)} onChange={(e) => toggleOffboardedSelect(row.id)} />
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="name-cell">
                                                                    <img
                                                                        src={row.avatar}
                                                                        alt="avatar"
                                                                        className="avatar"
                                                                        style={{ width: 28, height: 28, borderRadius: '50%', marginRight: 8, verticalAlign: 'middle' }}
                                                                    />
                                                                    {row.name || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                </div>
                                                            </td>
                                                            <td>{row.email || <span style={{ color: '#ADB5BD' }}>NA</span>}</td>
                                                            <td>{row.department || <span style={{ color: '#ADB5BD' }}>NA</span>}</td>
                                                            <td>{row.manager || <span style={{ color: '#ADB5BD' }}>NA</span>}</td>
                                                            <td>{row.jobTitle || <span style={{ color: '#ADB5BD' }}>NA</span>}</td>
                                                            <td>{row.entity || <span style={{ color: '#ADB5BD' }}>NA</span>}</td>
                                                            <td>{row.workplace || <span style={{ color: '#ADB5BD' }}>NA</span>}</td>
                                                        </tr>
                                                    );
                                                })}
                                                {offboardedPageData.length < PAGE_SIZE &&
                                                    [...Array(PAGE_SIZE - offboardedPageData.length)].map((_, idx) => (
                                                        <tr key={`empty-off-${idx}`} className="empty-row">
                                                            <td colSpan={8}>&nbsp;</td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* {offboardedFiltered > 0 && ( */}
                                    <div className="pagination">
                                        <div className="pagination-info">
                                            {`${(offboardedCurrentPage - 1) * PAGE_SIZE + 1} - ${Math.min(
                                                offboardedCurrentPage * PAGE_SIZE,
                                                offboardedFiltered.length
                                            )} of ${offboardedFiltered.length}`}
                                        </div>

                                        <Pagination
                                            currentPage={offboardedCurrentPage}
                                            totalPages={offboardedTotalPages}
                                            onPageChange={setOffboardedCurrentPage}
                                        />
                                    </div>
                                    {/* )} */}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {drawerOpen && selectedRow && (
                <>
                    <div className="drawer-overlay" />
                    <div className="entity-drawer" ref={drawerRef}>
                        <div className="drawer-header">
                            <h3>Employee Info</h3>
                            <button
                                className="close-btn"
                                onClick={() => {
                                    setDrawerOpen(false);
                                    setSelectedIds((ids) => ids.filter((id) => id !== selectedRow?.id));
                                    setSelectedRow(null);
                                }}
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <div className="main-drawer-inner">
                            <div className="drawer-title d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                    <img
                                        src="https://avatar.iran.liara.run/public"
                                        alt="avatar"
                                        className="avatar"
                                        style={{ width: 28, height: 28, borderRadius: '50%', marginRight: 8, verticalAlign: 'middle' }}
                                    />
                                    <h2 className="pb-0">{selectedRow.employee_first_name + ' ' + selectedRow.employee_last_name} </h2>
                                </div>
                                <div className="entities-tab" style={{ display: 'none' }}>
                                    <div className="toolbar">
                                        <div className="toolbar-right">
                                            <button className="icon-btn"><Edit2 size={18} /></button>
                                            <button className="icon-btn"><MoreHorizontal size={18} /></button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="drawer-tabs">
                                <button
                                    className={drawerTab === 'entity-info' ? 'active' : ''}
                                    onClick={() => setDrawerTab('entity-info')}
                                >
                                    Basic Info
                                </button>
                                <button
                                    className={drawerTab === 'contact-info' ? 'active' : ''}
                                    onClick={() => setDrawerTab('contact-info')}
                                >
                                    Contact Info
                                </button>
                                <button
                                    className={drawerTab === 'position-info' ? 'active' : ''}
                                    onClick={() => setDrawerTab('position-info')}
                                >
                                    Position Info
                                </button>
                                <button
                                    className={drawerTab === 'additional-info' ? 'active' : ''}
                                    onClick={() => setDrawerTab('additional-info')}
                                >
                                    Additional Info
                                </button>
                            </div>

                            <div className="drawer-content">
                                {drawerTab === 'entity-info' && (
                                    <div className="tab-panel entity-info-panel">
                                        <div className="field-block">
                                            <div className="field-label">Employee Name</div>
                                            <div className="field-value">{selectedRow.employee_first_name + ' ' + selectedRow.employee_last_name }</div>
                                        </div>
                                        <div className="field-block" style={{ position: "relative" }}>
                                            <div className="field-label">Open ID</div>
                                            <div className="field-value d-flex align-items-center gap-2">
                                                <span
                                                ref={openIdRef}
                                                onClick={handleCopy}
                                                style={{
                                                    color: copied ? "#60aaffff" : "#2C7BE5", // green if copied
                                                    cursor: "pointer",
                                                    textDecoration: "underline",
                                                    transition: "color 0.3s"
                                                }}
                                                >
                                                {selectedRow.uuid || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                </span>
                                                <div style={{ position: "relative" }}>
                                                {selectedRow.uuid &&<Copy
                                                    size={13}
                                                    onClick={handleCopy}
                                                    style={{ cursor: "pointer", color: copied ? "#60aaffff" : "#2C7BE5" }}
                                                />}
                                                {copied && <span style={{ fontSize: '12px', color: "#2C7BE5" }}>Copied!</span>}
                                                
                                                </div>
                                            </div>
                                        </div>
                                        <div className="field-block">
                                            <div className="field-label">Status</div>
                                            <div className="field-value">
                                                <span className={`status ${selectedRow.status_name.toLowerCase()}`}>{selectedRow.status_name || <span style={{ color: '#ADB5BD' }}>NA</span>}</span>
                                            </div>
                                        </div>
                                        <div className="field-block">
                                            <div className="field-label">Display Name</div>
                                            <div className="field-value">{selectedRow.display_name || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                        </div>
                                    </div>
                                )}
                                {drawerTab === 'contact-info' && (
                                    <div className="tab-panel contact-info-panel">
                                        <div className="field-block">
                                            <div className="field-label">Work Email</div>
                                            <div className="field-value">{selectedRow.company_email || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                        </div>
                                        <div className="field-block">
                                            <div className="field-label">Personal Email</div>
                                            <div className="field-value">{selectedRow.personal_email|| <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                        </div>
                                        <div className="field-block">
                                            <div className="field-label">Phone Number</div>
                                            <div className="field-value">{selectedRow.country_code_data?.country_code || <span style={{ color: '#ADB5BD' }}>NA</span>} {selectedRow.phone_number|| <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                        </div>
                                    </div>
                                )}
                                {drawerTab === 'position-info' && (
                                    <div className="tab-panel position-info-panel">
                                        <div className="field-block">
                                            <div className="field-label">Department</div>
                                            <div className="field-value">{selectedRow.departments_names || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                        </div>
                                        <div className="field-block">
                                            <div className="field-label">Manager</div>
                                            <div className="field-value">{selectedRow.manager || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                        </div>
                                        <div className="field-block">
                                            <div className="field-label">Entities</div>
                                            <div className="field-value">{selectedRow.entities_names || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                        </div>
                                        <div className="field-block">
                                            <div className="field-label">Workplace</div>
                                            <div className="field-value">{selectedRow.workplaces_names || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                        </div>
                                        <div className="field-block">
                                            <div className="field-label">Job Title</div>
                                            <div className="field-value">{selectedRow.job_title_data?.job_title_name || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                        </div>
                                        <div className="field-block">
                                            <div className="field-label">Onboard Date</div>
                                            <div className="field-value">{formattedDate || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                        </div>
                                    </div>
                                )}
                                {drawerTab === 'additional-info' && (
                                    <div className="tab-panel additional-info-panel">
                                        <p className="note-type-added">Note</p>
                                        <label className="no-description-cls when-no-description-cls-adding notes-preserve">{selectedRow.notes || <span style={{ color: '#ADB5BD' }}>NA</span>}</label>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    </>
                )}

                {bulkEditOpen && (
                    <>
                        <div className="drawer-overlay" />
                        <div className="entity-drawer main-company-body-inner new-main-entity-cls-adding-here" ref={bulkDrawerRef}>
                            <div className="drawer-header">
                                <h3>Batch Edit</h3>
                                <button className="close-btn" onClick={handleBulkCancel}><X size={24} /></button>
                            </div>
                            <div className="main-drawer-inner">
                                <div className="drawer-title info-inner">
                                    <label>Selected Employees</label>
                                    <div className="selected-tags">
                                        {selectedIds.map((id) => {
                                            const row = employeeData.find((d) => d.id === id);
                                            return (
                                                <div key={id} className="tag">
                                                    {row.name}
                                                    <span className="tag-close" onClick={() => removeSelectedId(id)}><X size={14} strokeWidth={3} /></span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="drawer-content">
                                    <div className="grid-info-adding">
                                        <div className="row gappingcls-added">
                                            <div className="col-lg-12 col-md-12 col-12">
                                                <div className="info-inner">
                                                    <label htmlFor="status" className="form-label">
                                                        Status <span className="error-color">*</span>
                                                    </label>
                                                    <div className="flexing-type-radio">
                                                        <div className="form-check">
                                                            <input className="form-check-input" type="radio" name="flexRadioDefault" id="flexRadioDefault1" />
                                                            <label className="form-check-label" htmlFor="flexRadioDefault1">Active</label>
                                                        </div>
                                                        <div className="form-check">
                                                            <input className="form-check-input" type="radio" name="flexRadioDefault" id="flexRadioDefault2" defaultChecked />
                                                            <label className="form-check-label" htmlFor="flexRadioDefault2">Inactive</label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row gappingcls-added">
                                            <div className="col-lg-12 col-md-12 col-12">
                                                <div className="info-inner">
                                                    <label htmlFor="departments" className="form-label d-flex align-items-center gap-1">
                                                        Departments
                                                        <Tooltip content="Select a new departments for all selected employees">
                                                            <Info size={16} className="info-icon" />
                                                        </Tooltip>
                                                    </label>
                                                    <select className="form-select new-form-select-cls" aria-label="Default select example">
                                                        <option defaultValue>Select Departments</option>
                                                        <option value="1">Jane Doe</option>
                                                        <option value="2">Serene</option>
                                                        <option value="3">Hau Jie</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row gappingcls-added">
                                            <div className="col-lg-12 col-md-12 col-12">
                                                <div className="info-inner">
                                                    <label htmlFor="manager" className="form-label d-flex align-items-center gap-1">
                                                        Manager
                                                        <Tooltip content="Choose a new manager location">
                                                            <Info size={16} className="info-icon" />
                                                        </Tooltip>
                                                    </label>
                                                    <select className="form-select new-form-select-cls" aria-label="Default select example">
                                                        <option defaultValue>Select manager</option>
                                                        <option value="1">Mister Mobile HQ</option>
                                                        <option value="2">Remote</option>
                                                        <option value="3">Branch Office</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row gappingcls-added">
                                            <div className="col-lg-12 col-md-12 col-12">
                                                <div className="info-inner">
                                                    <label htmlFor="entities" className="form-label d-flex align-items-center gap-1">
                                                        Entities
                                                        <Tooltip content="Choose a new entities location">
                                                            <Info size={16} className="info-icon" />
                                                        </Tooltip>
                                                    </label>
                                                    <select className="form-select new-form-select-cls" aria-label="Default select example">
                                                        <option defaultValue>Select entities</option>
                                                        <option value="1">Mister Mobile HQ</option>
                                                        <option value="2">Remote</option>
                                                        <option value="3">Branch Office</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row gappingcls-added">
                                            <div className="col-lg-12 col-md-12 col-12">
                                                <div className="info-inner">
                                                    <label htmlFor="entities" className="form-label d-flex align-items-center gap-1">
                                                        Workplaces
                                                        <Tooltip content="Choose a new workplaces location">
                                                            <Info size={16} className="info-icon" />
                                                        </Tooltip>
                                                    </label>
                                                    <select className="form-select new-form-select-cls" aria-label="Default select example">
                                                        <option defaultValue>Select workplaces</option>
                                                        <option value="1">Mister Mobile HQ</option>
                                                        <option value="2">Remote</option>
                                                        <option value="3">Branch Office</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row gappingcls-added">
                                            <div className="col-lg-12 col-md-12 col-12">
                                                <div className="info-inner">
                                                    <label htmlFor="entities" className="form-label d-flex align-items-center gap-1">
                                                        Job Title
                                                        <Tooltip content="Choose a new Job Title location">
                                                            <Info size={16} className="info-icon" />
                                                        </Tooltip>
                                                    </label>
                                                    <select className="form-select new-form-select-cls" aria-label="Default select example">
                                                        <option defaultValue>Select Job Title</option>
                                                        <option value="1">Mister Mobile HQ</option>
                                                        <option value="2">Remote</option>
                                                        <option value="3">Branch Office</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="drawer-footer">
                                    <button className="btn-cancel" onClick={handleBulkCancel}>Cancel</button>
                                    <button className="btn-save" onClick={handleBulkSave}>Save</button>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {offboardModalOpen && (
                    <>
                        <div className="drawer-overlay" />
                        <div
                            className="entity-drawer modal-popup-cls"
                            ref={offboardRef}
                            style={{
                                maxWidth: 650,
                                margin: '0 auto',
                                background: 'white',
                                borderRadius: 12,
                                zIndex: 1050,
                                padding: 24,
                                position: 'fixed',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.2)',
                                height: 'auto',
                                right: 'auto',
                                overflow: 'inherit',
                            }}
                        >
                            <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                                <button className="close-btn" onClick={() => setOffboardModalOpen(false)}><X size={20} /></button>
                            </div>
                            <h3 style={{ textAlign: 'left', color: '#212529', fontSize: '20px', fontWeight: '600', lineHeight: '24px', fontFamily: 'Montserrat', marginBottom: '5px' }}>
                                Are you sure you would like to offboard this employee?
                            </h3>
                            <p
                                style={{ marginBottom: '0', textAlign: 'left', color: '#6C757D', fontSize: '14px', fontWeight: '400', lineHeight: '20px', fontFamily: 'Montserrat' }}
                            >
                                If you would like to offboard this employee, confirm offboard date below
                            </p>
                            <div className="drawer-content">
                                <div className="row gappingcls-added">
                                    <div className="col-12">
                                        <div className="info-inner tab-panel">
                                            <div className="field-block">
                                                <div className="field-label" style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                    Offboard Date <span className="error-color" style={{ color: 'red' }}>*</span>
                                                    <Tooltip content="Choose offboard date for this employee">
                                                        <Info size={16} className="info-icon" />
                                                    </Tooltip>
                                                </div>
                                                <select className="form-select new-form-select-cls">
                                                    <option value="">Select Offboard Date</option>
                                                    <option value="2025-06-10">10 June 2025</option>
                                                    <option value="2025-06-15">15 June 2025</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="drawer-footer" style={{ paddingTop: '10px' }}>
                                <button className="btn-cancel" onClick={() => setOffboardModalOpen(false)}>Cancel</button>
                                <button className="btn-save" onClick={handleOffboardConfirm}>Offboard</button>
                            </div>
                        </div>
                    </>
                )}
        </div>
    )
}